import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Modal, Tag, message } from 'antd';
import { useHistory } from 'react-router-dom';
import { StarOutlined } from '@ant-design/icons';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { networkAction, fetchNetworkEntries } from '../../util/api';
import { userTypeOf } from '../../util/userType';
import { newIdempotencyKey } from '../../util/idempotencyKey';
import { teamRoleOf } from '../../util/teamRole';
import { talentSideFor, suppliersSideFor } from '../../containers/PreferredNetwork/networkPages';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

// Who may add to the organisation's list. The server decides; this only decides what is offered.
const MANAGER_ROLES = ['Admin', 'HR/Talent Acquisition', 'Tech Acquisition'];

const STATUS_COLOR = { requested: 'gold', accepted: 'green', rejected: 'red', expired: 'default', ended: 'default' };
const ROLE_ORDER = ['staffing', 'implementation'];

// The most people one status request names; the server answers at most this many.
const LOOKUP_BATCH = 48;

// Every card on a page asks for its person's entry when it first shows. The asks made in the same
// tick are sent together, one request per side and per 48 people, and each card is answered its
// own entry (or null when the person is not on the list).
let waiting = new Map();
let flushTimer = null;

const flushLookUps = () => {
  const batches = waiting;
  waiting = new Map();
  flushTimer = null;
  batches.forEach((callers, side) => {
    const partyIds = [...callers.keys()];
    for (let i = 0; i < partyIds.length; i += LOOKUP_BATCH) {
      const chunk = partyIds.slice(i, i + LOOKUP_BATCH);
      Promise.resolve()
        .then(() => fetchNetworkEntries({ side, partyIds: chunk }))
        .then(
        answer => {
          const byParty = new Map((answer?.items || []).map(item => [item?.party?.userId, item]));
          chunk.forEach(id => callers.get(id).forEach(({ resolve }) => resolve(byParty.get(id) || null)));
        },
        error => chunk.forEach(id => callers.get(id).forEach(({ reject }) => reject(error)))
      );
    }
  });
};

/** The person's entry on the signed-in organisation's list for this side, or null. */
export const lookUpNetworkEntry = (side, partyId) =>
  new Promise((resolve, reject) => {
    const callers = waiting.get(side) || new Map();
    waiting.set(side, callers);
    callers.set(partyId, [...(callers.get(partyId) || []), { resolve, reject }]);
    if (!flushTimer) flushTimer = setTimeout(flushLookUps, 0);
  });

// Each role the entry holds with its own status. An answer without role statuses is one role.
const roleStatesOf = entry => {
  if (!entry) return [];
  if (entry.roleStatus && Object.keys(entry.roleStatus).length) {
    return ROLE_ORDER.filter(role => entry.roleStatus[role]).map(role => ({ role, status: entry.roleStatus[role].status }));
  }
  const roles = entry.roles && entry.roles.length ? entry.roles : ['staffing'];
  return roles.map(role => ({ role, status: entry.status }));
};

// The side of the network "Add to Preferred" adds to: a candidate to talent (a supplier's
// consultants), a supplier to a customer's suppliers.
const sideForParty = (currentUser, partyKind) =>
  partyKind === 'agency' ? suppliersSideFor(userTypeOf(currentUser)) : talentSideFor(userTypeOf(currentUser));

/** Whether this signed-in user is offered "Add to Preferred" for a candidate, or for a supplier. */
export const canAddToPreferred = (currentUser, partyKind = 'candidate') =>
  !!sideForParty(currentUser, partyKind) && MANAGER_ROLES.includes(teamRoleOf(currentUser));

/**
 * The roles a supplier is added under, by the service type it declares: one role for a supplier
 * offering one, or null for one offering both (or saying neither), which is offered a choice.
 */
export const rolesForAgencyType = agencyType =>
  agencyType === 'Staffing Services' ? ['staffing'] : agencyType === 'Implementation Service' ? ['implementation'] : null;

/**
 * "Add to Preferred" for one candidate: adds them to the signed-in organisation's talent (a
 * supplier's consultants) as Staffing and shows "Staffing · Invited". When they are already on the
 * list it shows one badge per role with that role's status, and keeps offering the button only
 * while they hold no Staffing role that is still open. The status is asked for when the button
 * first shows (together with every other card on the page), so it shows before anything is
 * pressed; `lookUpStatus={false}` turns that off.
 *
 * For a supplier (`side="suppliers"`) the roles follow its declared service type
 * (`agencyType`): Staffing Services as staffing, Implementation Service as implementation; a supplier
 * offering both is offered Staffing, Implementation or both. A free plan that is full opens the
 * upgrade prompt.
 *
 * @param {Object} props
 * @param {string} props.partyId the candidate's user id, or the supplier company's owner
 * @param {Object} props.currentUser
 * @param {boolean} [props.lookUpStatus]
 * @param {'suppliers'} [props.side] add a supplier instead of a candidate
 * @param {string} [props.agencyType] the supplier's declared service type
 */
const AddToPreferredButton = props => {
  const { partyId, currentUser, lookUpStatus = true, size = 'large', side: askedSide, agencyType } = props;
  const intl = useIntl();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();
  const forSupplier = askedSide === 'suppliers';
  const side = sideForParty(currentUser, forSupplier ? 'agency' : 'candidate');
  const [entry, setEntry] = useState(null);
  const [busy, setBusy] = useState(false);
  const pressKey = useRef(null);

  useEffect(() => {
    if (!lookUpStatus || !side || !partyId) return undefined;
    let cancelled = false;
    lookUpNetworkEntry(side, partyId)
      .then(found => {
        if (!cancelled && found) setEntry(found);
      })
      .catch(() => {
        // Without the status the button is still offered; the server refuses a repeat.
      });
    return () => {
      cancelled = true;
    };
  }, [lookUpStatus, side, partyId]);

  if (!side || !partyId) return null;

  const roleStates = roleStatesOf(entry);
  // A role can be added while the entry does not hold it, or holds it only as Removed.
  const isOpen = role => roleStates.some(state => state.role === role && state.status !== 'ended');
  const declared = forSupplier ? rolesForAgencyType(agencyType) : ['staffing'];
  const choices = declared ? null : ROLE_ORDER.filter(role => !isOpen(role));
  const canAdd = declared ? declared.every(role => !isOpen(role)) : choices.length > 0;

  const showPlanLimit = () =>
    Modal.confirm({
      title: intl.formatMessage({ id: 'PreferredSuppliersPage.limit.title' }),
      content: intl.formatMessage({ id: 'PreferredSuppliersPage.limit.body' }),
      okText: intl.formatMessage({ id: 'PreferredTalentPage.upgrade.cta' }),
      onOk: () => history && history.push(createResourceLocatorString('StripePayoutPage', routeConfiguration, {}, {})),
    });

  // One key per press. A press that got no answer from the server (the connection dropped, or the
  // server failed) keeps its key, so pressing again retries it rather than inviting twice; once the
  // server has answered, the next press is a new one.
  const add = roles => {
    setBusy(true);
    if (!pressKey.current) pressKey.current = newIdempotencyKey();
    networkAction({ action: 'add', side, partyId, roles, idempotencyKey: pressKey.current })
      .then(answer => {
        pressKey.current = null;
        if (answer?.outcome === 'suppressed') {
          message.info(intl.formatMessage({ id: 'AddToPreferredButton.suppressed' }));
          return;
        }
        setEntry(answer?.entry || { status: 'requested', roles });
      })
      .catch(error => {
        // A refusal the server named is an answer; anything else may not have reached it.
        const answered = typeof error?.error === 'string' && error.error !== 'IDEMPOTENCY_IN_PROGRESS';
        if (answered) pressKey.current = null;
        if (error?.error === 'ALREADY_ON_LIST') {
          setEntry({ status: typeof error.status === 'string' ? error.status : 'requested', roleStatus: error.roleStatus });
        } else if (error?.error === 'PLAN_LIMIT') {
          showPlanLimit();
        } else if (error?.error === 'COMPANY_PROFILE_REQUIRED') {
          message.error(intl.formatMessage({ id: 'AddToPreferredButton.companyRequired' }));
        } else {
          message.error(intl.formatMessage({ id: 'AddToPreferredButton.failed' }));
        }
      })
      .finally(() => setBusy(false));
  };

  return (
    <>
      {roleStates.map(({ role, status }) => (
        <Tag key={role} color={STATUS_COLOR[status]} data-testid={`add-to-preferred-${partyId}-${role}`}>
          <span>
            <FormattedMessage id={`PreferredNetwork.role.${role}`} />
          </span>
          {' · '}
          <span>
            <FormattedMessage id={`PreferredNetwork.status.${status}`} />
          </span>
        </Tag>
      ))}
      {canAdd && declared ? (
        <Button size={size} icon={<StarOutlined />} loading={busy} onClick={() => add(declared)}>
          <FormattedMessage id="AddToPreferredButton.add" />
        </Button>
      ) : null}
      {canAdd && !declared ? (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              ...choices.map(role => ({ key: role, label: intl.formatMessage({ id: `AddToPreferredButton.choice.${role}` }) })),
              ...(choices.length === ROLE_ORDER.length ? [{ key: 'both', label: intl.formatMessage({ id: 'AddToPreferredButton.choice.both' }) }] : []),
            ],
            onClick: ({ key }) => add(key === 'both' ? [...ROLE_ORDER] : [key]),
          }}
        >
          <Button size={size} icon={<StarOutlined />} loading={busy}>
            <FormattedMessage id="AddToPreferredButton.add" />
          </Button>
        </Dropdown>
      ) : null}
    </>
  );
};

export default AddToPreferredButton;
